import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import {
  MunicipalTabulation,
  ProvincialDistrictTabulation,
  Tabulation,
  User,
  VoterTrackStats
} from '../../types';
import React, { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';

import TabulationGraph from './TabulationGraph';
import { db } from '../../firebase';
import useAuth from '../../hooks/useAuth';
import { useNavigate } from 'react-router';

const DistrictStats: React.FC = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [tabulations, setTabulations] = useState<Tabulation[]>([]);
  const [exitPollsStats, setExitPollsStats] = useState<VoterTrackStats>();
  const [finalReleasesStats, setFinalReleasesStats] = useState<VoterTrackStats>();
  const [stubReleasesStats, setStubReleasesStats] = useState<VoterTrackStats>();
  const [error, setError] = useState<string | null>(null);

  // Fetch stats from Firestore
  const fetchStatsFromFirestore = async () => {
    try {
      const exitPollsRef = doc(db, 'stats', 'exitPolls');
      const finalReleasesRef = doc(db, 'stats', 'finalReleases');
      const stubReleasesRef = doc(db, 'stats', 'stubReleases');

      const [exitPollsSnap, finalReleasesSnap, stubReleasesSnap] = await Promise.all([
        getDoc(exitPollsRef),
        getDoc(finalReleasesRef),
        getDoc(stubReleasesRef)
      ]);

      if (exitPollsSnap.exists()) {
        setExitPollsStats(exitPollsSnap.data() as VoterTrackStats);
      }
      if (finalReleasesSnap.exists()) {
        setFinalReleasesStats(finalReleasesSnap.data() as VoterTrackStats);
      }
      if (stubReleasesSnap.exists()) {
        setStubReleasesStats(stubReleasesSnap.data() as VoterTrackStats);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError('Failed to fetch stats');
    }
  };

  // Redirect if no view_throwing permission
  useEffect(() => {
    if (!loading && !user?.permissions.view_throwing) {
      navigate('/polls');
    }
  }, [user, loading, navigate]);

  const getTabulations = async (user: User): Promise<void> => {
    try {
      const districtsCol = collection(db, `tabulationsV2/${user.dashboard}/regions/REGION XIII/provinces/SURIGAO DEL NORTE/districts`);
      const districsSnap = await getDocs(districtsCol);
      const districtsTabs = districsSnap.docs.map((d) => d.data() as ProvincialDistrictTabulation);

      const cityTabs: Tabulation[] = [];
      for (const districtTab of districtsTabs) {
        const citySnap = await getDocs(collection(districtsCol, `${districtTab.district}/cities`));
        cityTabs.push(...citySnap.docs.map((d) => d.data() as MunicipalTabulation));
      }

      const allTabulations = [
        ...districtsTabs,
        ...cityTabs
      ];

      if (user.allowedTabulations) {
        const filteredTabulations = allTabulations.filter((tabulation) => {
          return user.allowedTabulations!.some(
            (allowedTabulation) => 
              tabulation.id === allowedTabulation.id && 
              tabulation.type === allowedTabulation.type
          );
        });
        setTabulations(filteredTabulations);
      } else {
        setTabulations(allTabulations);
      }
    } catch (err) {
      console.error('Error fetching tabulations:', err);
      setError('Failed to fetch tabulations');
    }
  };

  useEffect(() => {
    if (user) {
      fetchStatsFromFirestore().then(() => {
        getTabulations(user);
      })
    }
  }, [user]);

  // If loading, return null to prevent flash of content
  if (loading) {
    return null;
  }

  return (
    <Box position="relative" height="100%" overflow="hidden">
      {error && (
        <Typography color="error" textAlign="center">
          {error}
        </Typography>
      )}
      {tabulations.length > 0 && stubReleasesStats && finalReleasesStats && exitPollsStats ? (
        <Grid container spacing={1}>
          {tabulations.map((tabulation) => (
            <Grid item xs={12} md={tabulation.type === 'municipal' ? 6 : 12} key={tabulation.id}>
              <TabulationGraph
                key={tabulation.id}
                tabulation={tabulation}
                stubReleasesStats={stubReleasesStats}
                finalReleasesStats={finalReleasesStats}
                exitPollsStats={exitPollsStats}
                height={tabulation.type === 'municipal' ? 250 : 300}
                navUrl={tabulation.type === 'municipal' ? `/throwing/municipal/${(tabulation as MunicipalTabulation).city_municipality}_${(tabulation as MunicipalTabulation).province}` : undefined}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default DistrictStats;
